'use client';

import { usePulseTracker } from '@cineamo/pulse/react';
import { useQuery } from '@tanstack/react-query';
import type React from 'react';
import { ReactNode, useMemo } from 'react';

import { Client } from '@cineamo/api/constants/Client.enum';

import { AdvertisementProvider as SharedAdvertisementProvider } from '@cineamo/composables/src/advertisement/AdvertisementProvider';
import { advertisementPulseEvent } from '@cineamo/composables/src/advertisement/pulseEvent';
import {
    ADVERTISEMENT_TRACK_EVENT_NAME,
    AdvertisementDisplayAdapter
} from '@cineamo/composables/src/advertisement/types';
import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { getAdvertisementDisplay, postAdvertisementClick, postAdvertisementImpression } from '@/actions/advertisements';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

export const AdvertisementProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { cinema } = useDomainConfiguration();
    const { track } = useAnalytics();
    const pulseTracker = usePulseTracker();

    const { data: ipAddress } = useQuery({
        queryKey: ['ip-address'],
        queryFn: () => fetch('/api/ip-address').then((res) => res.json())
    });

    const adapter: AdvertisementDisplayAdapter = useMemo(
        () => ({
            fetchAdvertisement: getAdvertisementDisplay,
            postImpression: postAdvertisementImpression,
            postClick: postAdvertisementClick,
            displayLocation: Client.CinemaWebsite,
            cinemaId: cinema?.id,
            ipAddress: ipAddress?.ip,
            // TODO: change when we have multiple pages to show the ad
            referrerPage: 'homepage',
            enabled: !!cinema?.id,
            onTrack: (event, payload) => {
                track(ADVERTISEMENT_TRACK_EVENT_NAME[event], {
                    advertisementId: payload.advertisementId,
                    placement: payload.placement
                });
                // Mirrors apps/web (CIN-14644). Until this existed, cinema-website ad events reached
                // Vercel Analytics only and never Pulse, so they were absent from every Pulse report.
                //
                // Two caveats worth knowing when reading those reports.
                //
                // 1. The Pulse tracking route resolves the tenant from the OAuth client, so these
                //    events land in *this cinema's* tenant, not the platform tenant that apps/web
                //    writes to. A cross-channel view of one advertisement therefore has to span
                //    tenants — which is why `idx_interest_event_ad_cohort` deliberately does not lead
                //    with `tenant_id`.
                // 2. Cinema tenants are gated on activation: InterestTrackHandler drops every event
                //    unless this cinema is currently entitled to Pulse — a `cinema_product_entitlement`
                //    row that has started and not ended (the platform tenant is never gated). So for a
                //    cinema that has not opted in, this call is correct and still records nothing —
                //    check the entitlement before treating that as a tracking bug.
                pulseTracker.track(advertisementPulseEvent(event, payload));
            }
        }),
        [cinema?.id, ipAddress?.ip, track, pulseTracker]
    );

    return <SharedAdvertisementProvider adapter={adapter}>{children}</SharedAdvertisementProvider>;
};
